import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { PLATFORM_MAX_TRIAL_DAYS, platformCreateOrgInput, type PlatformCreateOrgInput, type PlatformCreateOrgResult } from '@barbeiropro/contracts';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, errorMessage, HttpError } from '@/lib/api';
import { applyServerError, zodForm } from '../catalog/lib';
import { CredentialFields, CredentialResult } from './credential-fields';
import { usePlans } from './plans-query';

/** Remove a senha quando o acesso é por link e troca '' por undefined, para o schema dos contracts validar. */
export function normalizeCredential<T extends { credential?: string; password?: string }>(v: T): T {
  const next = { ...v };
  if (next.credential !== 'password' || !next.password) delete next.password;
  return next;
}

const cleanDays = (n?: number) => (typeof n === 'number' && Number.isFinite(n) ? n : undefined);

const DEFAULTS: PlatformCreateOrgInput = {
  name: '',
  timezone: 'America/Sao_Paulo',
  owner: { name: '', email: '', credential: 'link', password: '' },
};

export function CreateOrgModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const plans = usePlans();
  const [planId, setPlanId] = useState('_default');
  const [result, setResult] = useState<PlatformCreateOrgResult | null>(null);
  const form = useForm<PlatformCreateOrgInput>({
    resolver: zodForm<PlatformCreateOrgInput>(platformCreateOrgInput, (v) => ({
      ...v,
      planId: planId === '_default' ? undefined : planId,
      trialDays: cleanDays(v.trialDays),
      owner: normalizeCredential(v.owner),
    })),
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    if (open) {
      setResult(null);
      setPlanId('_default');
      form.reset(DEFAULTS);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = useMutation({
    mutationFn: (v: PlatformCreateOrgInput) =>
      api.post<PlatformCreateOrgResult>('/platform/orgs', {
        ...v,
        planId: planId === '_default' ? undefined : planId,
        trialDays: cleanDays(v.trialDays),
        owner: normalizeCredential(v.owner),
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['platform'] });
      toast.success('Organização criada');
      setResult(r);
    },
    onError: (e) => {
      if (e instanceof HttpError && (e.code === 'platform_admin_cannot_join' || e.code === 'user_removed')) form.setError('owner.email', { message: e.message });
      else if (e instanceof HttpError && e.status === 422 && !Object.keys(e.fieldErrors()).length) toast.error(errorMessage(e));
      else applyServerError(form, e, { 'owner.email': 'owner.email' });
    },
  });
  const e = form.formState.errors;
  const credential = form.getValues('owner.credential');

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={result ? 'Organização criada' : 'Nova organização'}
      description={result ? undefined : 'Cria a barbearia com o primeiro administrador (dono) e um período de teste. Toda organização nasce com ao menos um dono ativo.'}
      footer={
        result ? (
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
            <Button
              onClick={() => {
                onOpenChange(false);
                navigate(`/plataforma/organizacoes/${result.id}`);
              }}
            >
              Ir para a organização
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" form="create-org" disabled={create.isPending}>
              {create.isPending ? 'Criando…' : 'Criar organização'}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <CredentialResult
          credential={credential}
          setupUrl={result.setupUrl}
          emailSent={result.emailSent}
          created={result.owner.created}
          who={result.owner.email}
        />
      ) : (
        <form id="create-org" onSubmit={form.handleSubmit((v) => create.mutate(v))} className="grid gap-6" noValidate>
          <section className="grid gap-4">
            <h3 className="text-sm font-semibold">Organização</h3>
            <Field id="co-name" label="Nome da barbearia" error={e.name?.message}>
              <Input id="co-name" autoComplete="off" aria-invalid={!!e.name} {...form.register('name')} />
            </Field>
            <Field id="co-tz" label="Fuso horário" error={e.timezone?.message}>
              <Input id="co-tz" {...form.register('timezone')} />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="co-plan" label="Plano">
                <Select value={planId} onValueChange={setPlanId}>
                  <SelectTrigger id="co-plan" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_default">Plano padrão</SelectItem>
                    {(plans.data ?? [])
                      .filter((p) => p.active)
                      .map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field id="co-trial" label="Dias de teste" hint="Em branco usa o teste do plano." error={e.trialDays?.message}>
                <Input id="co-trial" type="number" inputMode="numeric" min={0} max={PLATFORM_MAX_TRIAL_DAYS} {...form.register('trialDays', { setValueAs: (v) => (v === '' || v == null ? undefined : Number(v)) })} />
              </Field>
            </div>
          </section>
          <section className="grid gap-4">
            <h3 className="text-sm font-semibold">Administrador (dono)</h3>
            <Field id="co-owner" label="Nome" error={e.owner?.name?.message}>
              <Input id="co-owner" autoComplete="off" aria-invalid={!!e.owner?.name} {...form.register('owner.name')} />
            </Field>
            <Field id="co-email" label="E-mail" hint="Se o e-mail já tem conta, a pessoa vira dono da nova organização sem mudar a senha." error={e.owner?.email?.message}>
              <Input id="co-email" type="email" autoComplete="off" aria-invalid={!!e.owner?.email} {...form.register('owner.email')} />
            </Field>
            <CredentialFields form={form} base="owner." idPrefix="co" errorPassword={e.owner?.password?.message} />
          </section>
        </form>
      )}
    </FormModal>
  );
}
